import { CreditPurchaseStatus, TalentManagerEngagement } from '@talon/web/lib/api';

/**
 * Credit minutes an engagement can still take: what the agency bought, less approved time and time
 * awaiting approval (which has already claimed its credits). The API holds new time to the same sum.
 */
export const creditsAvailable = (e: TalentManagerEngagement): number =>
  e.credit_minutes_purchased -
  (e.timeEntries ?? []).filter((t) => t.ended_at).reduce((sum, t) => sum + t.minutes_billed, 0);

/** The shortest entry: time is billed in 15-minute steps */
export const MIN_ENTRY_MINUTES = 15;

/** Whether an engagement has credits for at least one more entry */
export const canLogTime = (e: TalentManagerEngagement): boolean => creditsAvailable(e) >= MIN_ENTRY_MINUTES;

/**
 * What the agency paid, in pennies, for `minutes` of an engagement's credits starting at minute
 * `from`. Credits are used oldest purchase first, each at the price it was bought at, so a later
 * rate change doesn't reprice time already paid for. Minutes no purchase covers are worth nothing.
 */
export const creditValuePennies = (e: TalentManagerEngagement, from: number, minutes: number): number => {
  const lots = (e.creditPurchases ?? [])
    .filter((p) => p.status === CreditPurchaseStatus.COMPLETED && p.minutes_purchased > 0)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  let start = 0;
  let pennies = 0;
  for (const lot of lots) {
    const end = start + lot.minutes_purchased;
    const used = Math.max(0, Math.min(end, from + minutes) - Math.max(start, from));
    pennies += (used * lot.amount_paid_in_pennies) / lot.minutes_purchased;
    start = end;
  }
  return pennies;
};
